import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiPackage, FiPlus, FiEdit, FiTrash2, FiCheck, FiX, FiEye,
  FiBook, FiLayers, FiDollarSign, FiCalendar, FiUsers,
  FiSearch, FiFilter, FiRefreshCw, FiAlertCircle, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import { apiUrls, getHttpHeaders } from '../config/api';
import RichTextEditor from './RichTextEditor';

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const Button = styled(motion.button)<{ variant?: 'primary' | 'secondary' | 'danger' | 'success' }>`
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          &:hover { background: #2563eb; }
        `;
      case 'success':
        return `
          background: #10b981;
          color: white;
          &:hover { background: #059669; }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover { background: #dc2626; }
        `;
      default:
        return `
          background: #f8fafc;
          color: #475569;
          border: 1px solid #e2e8f0;
          &:hover { background: #f1f5f9; }
        `;
    }
  }}
`;

const FilterBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  min-width: 250px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const PackageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const PackageCard = styled(motion.div)<{ status?: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => {
    switch (props.status) {
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      default: return '#f59e0b';
    }
  }};
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const PackageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const PackageTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 8px 0;
`;

const PackageStatus = styled.span<{ status?: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'approved':
        return 'background: #d1fae5; color: #065f46;';
      case 'rejected':
        return 'background: #fee2e2; color: #991b1b;';
      default:
        return 'background: #fef3c7; color: #92400e;';
    }
  }}
`;

const PackageDescription = styled.p`
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const PackageStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #475569;
`;

const PackageActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 24px 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BookSelector = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const BookList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const BookItem = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  
  &:hover {
    background: #f8fafc;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

// Types
interface Package {
  id: number;
  name: string;
  description: string;
  features: string;
  image: string;
  price: number;
  discount_price: number;
  duration_months: number;
  max_users: number;
  grade_id: number;
  subject_id?: number;
  seo_keywords?: string;
  approval_status: 'pending' | 'approved' | 'rejected';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  packageBooks?: any[];
  packageStructures?: any[];
}

interface Book {
  id: number;
  title: string;
  image: string;
  description: string;
  price: string;
  discount_price: string;
  grade_id: number | null;
  subject_id: number | null;
}

interface Grade {
  id: number;
  name: string;
}

interface Subject {
  id: number;
  name: string;
}

const PackageManagement: React.FC = () => {
  // State
  const [packages, setPackages] = useState<Package[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [allBooks, setAllBooks] = useState<Book[]>([]); // Store all books for filtering
  const [grades, setGrades] = useState<Grade[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [bookSearchTerm, setBookSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    features: '',
    image: '',
    price: '',
    discount_price: '',
    duration_months: '12',
    max_users: '1',
    grade_id: '',
    subject_id: '',
    seo_keywords: '',
    books: [] as number[],
    structures: [] as number[]
  });

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchPackages(),
        fetchBooks(),
        fetchGrades(),
        fetchSubjects()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${apiUrls.packages.list}?approval_status=all&is_active=all`, {
        headers: getHttpHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const pkgs = data.packages || [];
        console.log(`📦 Fetched ${pkgs.length} packages`);
        setPackages(pkgs);
      } else {
        console.error('❌ Failed to fetch packages:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching packages:', error);
    }
  };

  const fetchBooks = async () => {
    try {
      const response = await fetch(apiUrls.books.admin, {
        headers: getHttpHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const booksData = data.books || [];
        setAllBooks(booksData);
        setBooks(booksData); // Initially show all books
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await fetch(apiUrls.books.gradesAdmin, {
        headers: getHttpHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGrades(data.grades || []);
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await fetch(apiUrls.books.subjectsAdmin, {
        headers: getHttpHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSubjects(data.subjects || []);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  // Filter packages
  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || pkg.approval_status === statusFilter;
    const matchesGrade = gradeFilter === 'all' || pkg.grade_id?.toString() === gradeFilter;
    
    // Subject filter: Check if any of the package's books match the selected subject
    let matchesSubject = subjectFilter === 'all';
    if (!matchesSubject && pkg.packageBooks && pkg.packageBooks.length > 0) {
      matchesSubject = pkg.packageBooks.some((pb: any) => 
        pb.book?.subject_id?.toString() === subjectFilter
      );
    }
    
    return matchesSearch && matchesStatus && matchesGrade && matchesSubject;
  });

  // Apply sorting
  let sortedPackages = [...filteredPackages];
  if (sortColumn) {
    sortedPackages.sort((a, b) => {
      let aValue: any = '';
      let bValue: any = '';

      switch (sortColumn) {
        case 'Name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'Price':
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case 'Duration':
          aValue = a.duration_months || 0;
          bValue = b.duration_months || 0;
          break;
        case 'Status':
          aValue = a.approval_status || '';
          bValue = b.approval_status || '';
          break;
        case 'Books':
          aValue = a.packageBooks?.length || 0;
          bValue = b.packageBooks?.length || 0;
          break;
        case 'Created':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
  }

  // Calculate pagination
  const totalPages = Math.ceil(sortedPackages.length / itemsPerPage);
  const safePage = currentPage > totalPages && totalPages > 0 ? 1 : currentPage;
  const startIndex = (safePage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPackages = sortedPackages.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, gradeFilter, subjectFilter]);

  // Filter books based on search term
  const filteredBooks = books.filter(book =>
    book.title.toLowerCase().includes(bookSearchTerm.toLowerCase()) ||
    book.description?.toLowerCase().includes(bookSearchTerm.toLowerCase())
  );

  // Handle form submission
  const handleCreatePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(apiUrls.packages.create, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price),
          discount_price: formData.discount_price ? parseFloat(formData.discount_price) : null,
          duration_months: parseInt(formData.duration_months),
          max_users: parseInt(formData.max_users),
          grade_id: formData.grade_id ? parseInt(formData.grade_id) : null,
          subject_id: formData.subject_id ? parseInt(formData.subject_id) : null,
          books: formData.books.map(bookId => ({ book_id: bookId }))
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Package created:', result);
        
        // Reset filters to show all packages
        setStatusFilter('all');
        setGradeFilter('all');
        setSearchTerm('');
        
        // Refresh package list
        await fetchPackages();
        
        // Small delay to ensure backend has processed
        setTimeout(async () => {
          await fetchPackages();
          console.log('🔄 Packages refreshed');
        }, 500);
        
        setShowCreateModal(false);
        resetForm();
        alert(`Package created successfully! Look for: ${formData.name}`);
      } else {
        const error = await response.json();
        alert(`Error creating package: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating package:', error);
      alert('Error creating package. Please try again.');
    }
  };

  const handleUpdatePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPackage) return;
    
    try {
      const response = await fetch(`${apiUrls.packages.byId(selectedPackage.id)}`, {
        method: 'PUT',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price),
          discount_price: formData.discount_price ? parseFloat(formData.discount_price) : null,
          duration_months: parseInt(formData.duration_months),
          max_users: parseInt(formData.max_users),
          grade_id: formData.grade_id ? parseInt(formData.grade_id) : null,
          subject_id: formData.subject_id ? parseInt(formData.subject_id) : null,
          books: formData.books.map(bookId => ({ book_id: bookId }))
        })
      });

      if (response.ok) {
        await fetchPackages();
        setShowEditModal(false);
        setSelectedPackage(null);
        resetForm();
        alert('Package updated successfully!');
      } else {
        const error = await response.json();
        alert(`Error updating package: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating package:', error);
      alert('Error updating package. Please try again.');
    }
  };

  // Handle package approval
  const handleApprovePackage = async (packageId: number, status: 'approved' | 'rejected') => {
    try {
      const response = await fetch(apiUrls.packages.approve(packageId), {
        method: 'PATCH',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          approval_status: status,
          approved_by: 1 // TODO: Get from user context
        })
      });

      if (response.ok) {
        await fetchPackages();
        alert(`Package ${status} successfully!`);
      } else {
        const error = await response.json();
        alert(`Error ${status === 'approved' ? 'approving' : 'rejecting'} package: ${error.message}`);
      }
    } catch (error) {
      console.error('Error updating package approval:', error);
      alert('Error updating package approval. Please try again.');
    }
  };

  // Handle package deletion
  const handleDeletePackage = async (packageId: number) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      const response = await fetch(apiUrls.packages.delete(packageId), {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        await fetchPackages();
        alert('Package deleted successfully!');
      } else {
        alert(`Error deleting package: ${data.error || data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting package:', error);
      alert('Error deleting package. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      features: '',
      image: '',
      price: '',
      discount_price: '',
      duration_months: '12',
      max_users: '1',
      grade_id: '',
      subject_id: '',
      seo_keywords: '',
      books: [],
      structures: []
    });
    // Reset books to show all books when form is reset
    setBooks(allBooks);
    setBookSearchTerm(''); // Clear book search when form is reset
  };

  const handleBookSelection = (bookId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      books: checked 
        ? [...prev.books, bookId]
        : prev.books.filter(id => id !== bookId)
    }));
  };

  // Handle grade change and filter books
  const handleGradeChange = (gradeId: string) => {
    setFormData(prev => ({ ...prev, grade_id: gradeId, books: [] })); // Clear selected books when grade changes
    setBookSearchTerm(''); // Clear book search when grade changes
    
    if (gradeId) {
      // Filter books by selected grade - handle null/undefined grade_id
      const filteredBooks = allBooks.filter(book => 
        book.grade_id && book.grade_id.toString() === gradeId
      );
      setBooks(filteredBooks);
    } else {
      // Show all books if no grade selected
      setBooks(allBooks);
    }
  };

  // Handle book search within the filtered books
  const handleBookSearch = (searchTerm: string) => {
    setBookSearchTerm(searchTerm);
    
    if (!formData.grade_id) {
      // If no grade selected, search all books
      if (searchTerm.trim()) {
        const searchedBooks = allBooks.filter(book =>
          book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (book.description && book.description.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        setBooks(searchedBooks);
      } else {
        setBooks(allBooks);
      }
    } else {
      // If grade is selected, first filter by grade, then by search term
      const gradeFilteredBooks = allBooks.filter(book => 
        book.grade_id && book.grade_id.toString() === formData.grade_id
      );
      
      if (searchTerm.trim()) {
        const searchedBooks = gradeFilteredBooks.filter(book =>
          book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (book.description && book.description.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        setBooks(searchedBooks);
      } else {
        setBooks(gradeFilteredBooks);
      }
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>
          <FiRefreshCw size={24} />
          <span style={{ marginLeft: '12px' }}>Loading packages...</span>
        </LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <FiPackage />
          Package Management
        </Title>
        <Actions>
          <Button onClick={loadData}>
            <FiRefreshCw />
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <FiPlus />
            Create Package
          </Button>
        </Actions>
      </Header>

      {/* Pagination Controls */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap'
      }}>
        {/* Items per page selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
            Show:
          </label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{
              padding: '6px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ fontSize: '14px', color: '#6b7280' }}>per page</span>
        </div>

        {totalPages > 1 && (
          <>
            <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safePage === 1}
              style={{
                padding: '8px 16px',
                backgroundColor: safePage === 1 ? '#e5e7eb' : '#3b82f6',
                color: safePage === 1 ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s'
              }}
            >
              <FiChevronLeft size={16} />
              Previous
            </button>

            <div style={{ 
              padding: '8px 16px', 
              backgroundColor: 'white',
              border: '2px solid #3b82f6',
              borderRadius: '6px',
              color: '#3b82f6',
              fontSize: '14px',
              fontWeight: '700',
              whiteSpace: 'nowrap'
            }}>
              Page {safePage} of {totalPages}
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              style={{
                padding: '8px 16px',
                backgroundColor: safePage === totalPages ? '#e5e7eb' : '#3b82f6',
                color: safePage === totalPages ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s'
              }}
            >
              Next
              <FiChevronRight size={16} />
            </button>
          </>
        )}
      </div>

      <FilterBar>
        <SearchInput
          type="text"
          placeholder="Search packages..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </Select>
        <Select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)}>
          <option value="all">All Grades</option>
          {grades.map(grade => (
            <option key={grade.id} value={grade.id}>{grade.name}</option>
          ))}
        </Select>
        <Select value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}>
          <option value="all">All Subjects</option>
          {subjects.map(subject => (
            <option key={subject.id} value={subject.id}>{subject.name}</option>
          ))}
        </Select>
        <Select 
          value={sortColumn} 
          onChange={(e) => {
            setSortColumn(e.target.value);
            setSortDirection('asc');
          }}
        >
          <option value="">Sort By...</option>
          <option value="Name">Name</option>
          <option value="Price">Price</option>
          <option value="Duration">Duration</option>
          <option value="Status">Status</option>
          <option value="Books">Number of Books</option>
          <option value="Created">Created Date</option>
        </Select>
        {sortColumn && (
          <Select 
            value={sortDirection} 
            onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}
          >
            <option value="asc">↑ Ascending</option>
            <option value="desc">↓ Descending</option>
          </Select>
        )}
      </FilterBar>

      {filteredPackages.length === 0 ? (
        <EmptyState>
          <FiPackage size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <h3>No packages found</h3>
          <p>Create your first package to get started.</p>
        </EmptyState>
      ) : (
        <PackageGrid>
          {paginatedPackages.map(pkg => (
            <PackageCard
              key={pkg.id}
              status={pkg.approval_status}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <PackageHeader>
                <div>
                  <PackageTitle>{pkg.name}</PackageTitle>
                  <PackageStatus status={pkg.approval_status}>
                    {pkg.approval_status}
                  </PackageStatus>
                </div>
              </PackageHeader>

              <PackageDescription>
                {pkg.description || 'No description provided'}
              </PackageDescription>

              <PackageStats>
                <StatItem>
                  <FiDollarSign size={16} />
                  ${pkg.price}
                  {pkg.discount_price && (
                    <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>
                      ${pkg.discount_price}
                    </span>
                  )}
                </StatItem>
                <StatItem>
                  <FiCalendar size={16} />
                  {pkg.duration_months} months
                </StatItem>
                <StatItem>
                  <FiUsers size={16} />
                  Max {pkg.max_users} user{pkg.max_users !== 1 ? 's' : ''}
                </StatItem>
                <StatItem>
                  <FiBook size={16} />
                  {pkg.packageBooks?.length || 0} book{pkg.packageBooks?.length !== 1 ? 's' : ''}
                </StatItem>
              </PackageStats>

              <PackageActions>
                <Button 
                  onClick={() => {
                    // Show detailed package info
                    const bookNames = pkg.packageBooks?.map(pb => pb.book?.title || `Book ${pb.book_id}`).join('\n  • ') || 'No books';
                    const structureNames = pkg.packageStructures?.map(ps => ps.structure?.name || `Structure ${ps.structure_id}`).join('\n  • ') || 'No structures';
                    
                    alert(`📦 Package Details:\n\nName: ${pkg.name}\nID: ${pkg.id}\nPrice: $${pkg.price}\nDuration: ${pkg.duration_months} months\nMax Users: ${pkg.max_users}\n\n📚 Attached Books (${pkg.packageBooks?.length || 0}):\n  • ${bookNames}\n\n🏗️ Content Structures (${pkg.packageStructures?.length || 0}):\n  • ${structureNames}`);
                  }}
                  title="View package details"
                >
                  <FiEye size={16} />
                </Button>
                <Button 
                  onClick={() => {
                    // Set package data and open edit modal
                    setSelectedPackage(pkg);
                    setFormData({
                      name: pkg.name,
                      description: pkg.description || '',
                      features: pkg.features || '',
                      image: pkg.image || '',
                      price: pkg.price.toString(),
                      discount_price: pkg.discount_price?.toString() || '',
                      duration_months: pkg.duration_months.toString(),
                      max_users: pkg.max_users.toString(),
                      grade_id: pkg.grade_id?.toString() || '',
                      subject_id: pkg.subject_id?.toString() || '',
                      seo_keywords: pkg.seo_keywords || '',
                      books: pkg.packageBooks?.map((pb: any) => pb.book_id) || [],
                      structures: pkg.packageStructures?.map((ps: any) => ps.structure_id) || []
                    });
                    setShowEditModal(true);
                  }}
                  title="Edit package"
                >
                  <FiEdit size={16} />
                </Button>
                {pkg.approval_status === 'pending' && (
                  <>
                    <Button 
                      variant="success" 
                      onClick={() => handleApprovePackage(pkg.id, 'approved')}
                    >
                      <FiCheck size={16} />
                    </Button>
                    <Button 
                      variant="danger" 
                      onClick={() => handleApprovePackage(pkg.id, 'rejected')}
                    >
                      <FiX size={16} />
                    </Button>
                  </>
                )}
                <Button 
                  variant="danger" 
                  onClick={() => handleDeletePackage(pkg.id)}
                >
                  <FiTrash2 size={16} />
                </Button>
              </PackageActions>
            </PackageCard>
          ))}
        </PackageGrid>
      )}

      {/* Create Package Modal */}
      {showCreateModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <ModalHeader>
              <ModalTitle>Create New Package</ModalTitle>
              <Button onClick={() => setShowCreateModal(false)}>
                <FiX />
              </Button>
            </ModalHeader>

            <form onSubmit={handleCreatePackage}>
              <FormGrid>
                <FormField>
                  <Label>Package Name *</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </FormField>
                <FormField>
                  <Label>Grade</Label>
                  <Select
                    value={formData.grade_id}
                    onChange={(e) => handleGradeChange(e.target.value)}
                  >
                    <option value="">Select Grade</option>
                    {grades.map(grade => (
                      <option key={grade.id} value={grade.id}>{grade.name}</option>
                    ))}
                  </Select>
                </FormField>
              </FormGrid>

              <FormGrid>
                <FormField>
                  <Label>Subject</Label>
                  <Select
                    value={formData.subject_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject_id: e.target.value }))}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </Select>
                </FormField>
              </FormGrid>

              <FormField style={{ marginBottom: '24px' }}>
                <Label>Description</Label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                  placeholder="Describe what's included in this package..."
                  showImageUpload={false}
                  minHeight="150px"
                />
              </FormField>

              <FormField style={{ marginBottom: '24px' }}>
                <Label>SEO Keywords:</Label>
                <TextArea
                  value={formData.seo_keywords}
                  onChange={(e) => setFormData(prev => ({ ...prev, seo_keywords: e.target.value }))}
                  placeholder="Enter SEO keywords, tags, or search terms (one per line or comma-separated)..."
                  rows={4}
                />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                  💡 Tip: Add keywords that students/parents might search for (e.g., "math bundle", "algebra package", "grade 8 math")
                </div>
              </FormField>

              <FormGrid>
                <FormField>
                  <Label>Price *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    required
                  />
                </FormField>
                <FormField>
                  <Label>Discount Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.discount_price}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_price: e.target.value }))}
                  />
                </FormField>
                <FormField>
                  <Label>Duration (months)</Label>
                  <Input
                    type="number"
                    value={formData.duration_months}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_months: e.target.value }))}
                  />
                </FormField>
                <FormField>
                  <Label>Max Users</Label>
                  <Input
                    type="number"
                    value={formData.max_users}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_users: e.target.value }))}
                  />
                </FormField>
              </FormGrid>

              <SectionTitle>
                <FiBook />
                Select Books {formData.grade_id && `(Grade: ${grades.find(g => g.id.toString() === formData.grade_id)?.name})`}
              </SectionTitle>
              
              {formData.grade_id && (
                <div style={{ marginBottom: '16px' }}>
                  <Input
                    type="text"
                    placeholder="Search books by title or description..."
                    value={bookSearchTerm}
                    onChange={(e) => handleBookSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}
              
              <BookSelector>
                {!formData.grade_id && (
                  <div style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    color: '#64748b',
                    fontStyle: 'italic'
                  }}>
                    Please select a grade first to see available books
                  </div>
                )}
                {formData.grade_id && books.length === 0 && (
                  <div style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    color: '#64748b',
                    fontStyle: 'italic'
                  }}>
                    No books available for the selected grade
                  </div>
                )}
                {books.length > 0 && (
                  <>
                    {bookSearchTerm && (
                      <div style={{
                        padding: '8px 12px',
                        marginBottom: '12px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#6b7280'
                      }}>
                        {books.length} book{books.length !== 1 ? 's' : ''} found for "{bookSearchTerm}"
                      </div>
                    )}
                    <BookList>
                    {books.map(book => {
                      const subject = subjects.find(s => s.id === book.subject_id);
                      return (
                        <BookItem key={book.id}>
                          <input
                            type="checkbox"
                            checked={formData.books.includes(book.id)}
                            onChange={(e) => handleBookSelection(book.id, e.target.checked)}
                          />
                          <div style={{ flex: 1 }}>
                            <strong>{book.title}</strong>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                              <span>Subject: {subject?.name || 'Unknown'}</span>
                              <span style={{ margin: '0 8px' }}>•</span>
                              <span>Grade: {grades.find(g => g.id === book.grade_id)?.name || 'Unknown'}</span>
                              <span style={{ margin: '0 8px' }}>•</span>
                              <span>Price: ${book.price}</span>
                              {book.discount_price && book.discount_price !== book.price && (
                                <span> (Discounted: ${book.discount_price})</span>
                              )}
                            </div>
                            {book.description && (
                              <div style={{ 
                                fontSize: '11px', 
                                color: '#9ca3af', 
                                marginTop: '4px',
                                maxHeight: '40px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {book.description.replace(/<[^>]*>/g, '').substring(0, 100)}...
                              </div>
                            )}
                          </div>
                        </BookItem>
                      );
                    })}
                  </BookList>
                  </>
                )}
              </BookSelector>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  Create Package
                </Button>
              </div>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Edit Package Modal */}
      {showEditModal && selectedPackage && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <ModalHeader>
              <h2>Edit Package: {selectedPackage.name}</h2>
              <Button onClick={() => {
                setShowEditModal(false);
                setSelectedPackage(null);
              }}>
                <FiX />
              </Button>
            </ModalHeader>

            <form onSubmit={handleUpdatePackage}>
              <FormGrid>
                <FormField>
                  <Label>Package Name *</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </FormField>

                <FormField>
                  <Label>Grade</Label>
                  <Select
                    value={formData.grade_id}
                    onChange={(e) => setFormData({ ...formData, grade_id: e.target.value })}
                  >
                    <option value="">Select Grade</option>
                    {grades.map(grade => (
                      <option key={grade.id} value={grade.id}>{grade.name}</option>
                    ))}
                  </Select>
                </FormField>

                <FormField>
                  <Label>Subject</Label>
                  <Select
                    value={formData.subject_id}
                    onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </Select>
                </FormField>

                <FormField style={{ gridColumn: '1 / -1' }}>
                  <Label>Description</Label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    placeholder="Describe what's included in this package..."
                    showImageUpload={false}
                    minHeight="150px"
                  />
                </FormField>

                <FormField style={{ gridColumn: '1 / -1' }}>
                  <Label>SEO Keywords:</Label>
                  <TextArea
                    value={formData.seo_keywords}
                    onChange={(e) => setFormData({ ...formData, seo_keywords: e.target.value })}
                    placeholder="Enter SEO keywords, tags, or search terms (one per line or comma-separated)..."
                    rows={4}
                  />
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                    💡 Tip: Add keywords that students/parents might search for (e.g., "math bundle", "algebra package", "grade 8 math")
                  </div>
                </FormField>

                <FormField>
                  <Label>Price ($) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </FormField>

                <FormField>
                  <Label>Discount Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.discount_price}
                    onChange={(e) => setFormData({ ...formData, discount_price: e.target.value })}
                  />
                </FormField>

                <FormField>
                  <Label>Duration (months)</Label>
                  <Input
                    type="number"
                    value={formData.duration_months}
                    onChange={(e) => setFormData({ ...formData, duration_months: e.target.value })}
                  />
                </FormField>

                <FormField>
                  <Label>Max Users</Label>
                  <Input
                    type="number"
                    value={formData.max_users}
                    onChange={(e) => setFormData({ ...formData, max_users: e.target.value })}
                  />
                </FormField>

                <BookSelector>
                  <Label>Select Books for This Package</Label>
                  
                  {/* Show currently attached books with remove buttons */}
                  {formData.books.length > 0 && (
                    <div style={{ 
                      padding: '12px', 
                      background: '#ecfdf5', 
                      border: '1px solid #10b981', 
                      borderRadius: '6px', 
                      marginBottom: '12px' 
                    }}>
                      <div style={{ fontWeight: '600', color: '#059669', marginBottom: '8px', fontSize: '13px' }}>
                        ✅ Currently Attached: {formData.books.length} book{formData.books.length !== 1 ? 's' : ''}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {allBooks.filter(b => formData.books.includes(b.id)).map(book => (
                          <div key={book.id} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 8px',
                            background: 'white',
                            borderRadius: '4px',
                            border: '1px solid #d1fae5'
                          }}>
                            <span style={{ fontSize: '13px', color: '#047857', fontWeight: '500' }}>
                              {book.title}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleBookSelection(book.id, false)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                fontWeight: '600'
                              }}
                              title={`Remove ${book.title} from package`}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <SearchInput
                    type="text"
                    placeholder="Search books..."
                    value={bookSearchTerm}
                    onChange={(e) => setBookSearchTerm(e.target.value)}
                  />
                  {bookSearchTerm && filteredBooks.length < books.length && (
                    <div style={{ padding: '8px', fontSize: '12px', color: '#6b7280', background: '#f9fafb', borderRadius: '4px', marginBottom: '8px' }}>
                      {filteredBooks.length} book{filteredBooks.length !== 1 ? 's' : ''} found for "{bookSearchTerm}"
                    </div>
                  )}
                  <BookList>
                    {filteredBooks.map(book => (
                      <BookItem key={book.id}>
                        <input
                          type="checkbox"
                          checked={formData.books.includes(book.id)}
                          onChange={(e) => handleBookSelection(book.id, e.target.checked)}
                        />
                        <div style={{ flex: 1 }}>
                          <strong>{book.title}</strong>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            ${book.price}
                          </div>
                        </div>
                      </BookItem>
                    ))}
                  </BookList>
                  {filteredBooks.length === 0 && bookSearchTerm && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
                      No books found matching "{bookSearchTerm}"
                    </div>
                  )}
                </BookSelector>
              </FormGrid>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <Button type="button" onClick={() => {
                  setShowEditModal(false);
                  setSelectedPackage(null);
                }}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  Update Package
                </Button>
              </div>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default PackageManagement;
